import { gql, useQuery } from '@apollo/client'
import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import {
  Breadcrumb,
  ErrorText,
  FixedWidthPageContainer,
  IterationDisplay,
  Table,
} from '../common/common-components'
import { humanizedDurationFromNow } from '../common/data-transforms'
import {
  MeasurementWorkerSummaryQuery,
  MeasurementWorkerSummaryQueryVariables,
} from '../common/generated'
import { transformMeasurerVersion } from '../common/measurer-version'
import { MeasurerVersionDisplay } from '../common/measurer-version-display'
import { StatusBadge } from '../common/status-badge'

const MEASUREMENT_WORKER_SUMMARY_QUERY = gql`
  query MeasurementWorkerSummary {
    allCommits(orderBy: ID_DESC, first: 30) {
      nodes {
        id
        created
        measurerVersionByMeasurerVersionId {
          measurerByMeasurerId {
            name
            repoSlug
          }
          sha1
          tagName
          stateMachineStateId
        }
        measurementInvocationsByCommitId {
          totalCount
        }
        measurementInvocationIterationsByMeasurementInvocationCommitId {
          nodes {
            measurementStudyByMeasurementStudyId {
              id
              name
            }
            measurementInvocationIteration
          }
        }
        commitByParentCommitId {
          id
          created
          measurementInvocationIterationsByMeasurementInvocationCommitId {
            nodes {
              measurementInvocationIteration
              measurementStudyByMeasurementStudyId {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`

const MeasurementWorkerTable = styled(Table)`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  th:nth-child(4),
  td:nth-child(4),
  th:nth-child(5),
  td:nth-child(5) {
    width: 400px;
  }

  td {
    padding: 8px;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: none;
  }
`

const StatusBadgeContainer = styled.div`
  margin-top: 4px;
`

const StudyList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin: 4px 0;
  }
`

function StudiesColumn({
  nodes,
  isOrphaned = false,
}: {
  nodes: Array<{
    measurementStudyByMeasurementStudyId: {
      id: number
      name: string
    }
    measurementInvocationIteration: number | null
  }> | null
  isOrphaned?: boolean
}): JSX.Element {
  if (isOrphaned || !nodes || nodes.length === 0) {
    return <span>N/A</span>
  }

  return (
    <StudyList>
      {nodes.map(node => (
        <li key={node.measurementStudyByMeasurementStudyId.id}>
          <Link to={`/studies/${node.measurementStudyByMeasurementStudyId.id}`}>
            {node.measurementStudyByMeasurementStudyId.name}
          </Link>
          {node.measurementInvocationIteration !== null && (
            <>
              {' '}
              <IterationDisplay
                iteration={node.measurementInvocationIteration}
              />
            </>
          )}
        </li>
      ))}
    </StudyList>
  )
}

export function MeasurementWorkerSummary(): JSX.Element {
  const { loading, error, data } = useQuery<
    MeasurementWorkerSummaryQuery,
    MeasurementWorkerSummaryQueryVariables
  >(MEASUREMENT_WORKER_SUMMARY_QUERY)

  const commits = (data?.allCommits.nodes ?? []).filter(
    commit => commit.measurerVersionByMeasurerVersionId !== null
  )

  return (
    <FixedWidthPageContainer>
      <Breadcrumb>
        <Link to="/">Home</Link> {'>'} Recent measurement work
      </Breadcrumb>
      <h1>Recent measurement work</h1>

      {error && <ErrorText>Oh no! {error.message}</ErrorText>}
      {loading && <p>Loading...</p>}

      {!loading && (
        <MeasurementWorkerTable>
          <thead>
            <tr>
              <th>ID</th>
              <th>Date</th>
              <th>Measurement Invocations</th>
              <th>Measurement Study</th>
              <th>Parent Commit Measurement Study</th>
              <th>Measurer Version</th>
              <th>Deploy Status</th>
            </tr>
          </thead>
          <tbody>
            {commits.map(commit => {
              const measurerVersion = commit.measurerVersionByMeasurerVersionId
              if (!measurerVersion) {
                return null
              }

              const transformedVersion = transformMeasurerVersion({
                measurerName: measurerVersion.measurerByMeasurerId.name,
                functionName: null,
                repoSlug: measurerVersion.measurerByMeasurerId.repoSlug,
                sha1: measurerVersion.sha1,
                tagName: measurerVersion.tagName,
                commitDate: null,
                commitMessage: null,
              })

              return (
                <tr key={commit.id}>
                  <td>{commit.id}</td>
                  <td>{humanizedDurationFromNow(commit.created)}</td>
                  <td>
                    {commit.measurementInvocationsByCommitId.totalCount.toLocaleString()}
                  </td>
                  <td>
                    <StudiesColumn
                      nodes={
                        commit
                          .measurementInvocationIterationsByMeasurementInvocationCommitId
                          .nodes
                      }
                    />
                  </td>
                  <td>
                    <StudiesColumn
                      nodes={
                        commit.commitByParentCommitId
                          ?.measurementInvocationIterationsByMeasurementInvocationCommitId
                          .nodes ?? null
                      }
                      isOrphaned={!commit.commitByParentCommitId}
                    />
                  </td>
                  <td>
                    <MeasurerVersionDisplay
                      measurerVersion={transformedVersion}
                    />
                  </td>
                  <td>
                    <StatusBadgeContainer>
                      <StatusBadge state={measurerVersion.stateMachineStateId}>
                        {measurerVersion.stateMachineStateId}
                      </StatusBadge>
                    </StatusBadgeContainer>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </MeasurementWorkerTable>
      )}
    </FixedWidthPageContainer>
  )
}
